<template>
  <div class="coupon-page">
    <APageHead title="쿠폰 관리" sub="할인 쿠폰을 생성하고 관리합니다.">
      <template #actions>
        <ABtn icon="download" @click="downloadExcel">엑셀 다운로드</ABtn>
        <ABtn icon="dynamic_feed" @click="openBulkModal">대량 발급</ABtn>
        <ABtn kind="primary" icon="add" @click="openCreateModal">쿠폰 등록</ABtn>
      </template>
    </APageHead>

    <!-- KPI -->
    <div class="coupon-page__stats">
      <ACard><AStat label="전체 쿠폰" :value="formatNumber(stats.total)" unit="개" /></ACard>
      <ACard><AStat label="범용 쿠폰" :value="formatNumber(stats.general)" unit="개" /></ACard>
      <ACard><AStat label="지정 쿠폰" :value="formatNumber(stats.assigned)" unit="개" /></ACard>
      <ACard><AStat label="이번 달 발급" :value="formatNumber(stats.thisMonth)" unit="개" /></ACard>
    </div>

    <!-- 탭 -->
    <div class="p-tabs coupon-page__tabs">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        type="button"
        :class="['p-tabs__tab', { 'is-active': activeTab === tab.value }]"
        @click="handleTabChange(tab.value)"
      >
        <i class="material-icons-round coupon-page__tab-icon">{{ tab.icon }}</i>
        {{ tab.label }}
        <span v-if="tab.count > 0" class="p-tabs__count">{{ tab.count }}</span>
      </button>
    </div>

    <!-- 검색 -->
    <ACard overflow="visible">
      <SearchArea :search-fields="searchFields" @search="handleSearch" />
    </ACard>

    <!-- 쿠폰 목록 -->
    <ACard>
      <template #title>
        <span class="coupon-page__sec-title">
          <i class="material-icons-round coupon-page__sec-icon">confirmation_number</i>
          쿠폰 목록
        </span>
      </template>
      <template #sub>등록된 쿠폰을 관리합니다.</template>
      <template #action>
        <ABtn
          v-if="selectedItems.length > 0"
          size="sm" kind="danger" icon="delete"
          @click="deleteBulk"
        >선택 삭제 ({{ selectedItems.length }})</ABtn>
        <APill kind="info">총 {{ totalCount }}개</APill>
      </template>

      <AEmpty v-if="tableData.length === 0" title="등록된 쿠폰이 없어요" sub="새 쿠폰을 등록하거나 필터를 변경해보세요." />

      <ATable v-else :columns="columns" :rows="tableData" row-key="idx">
        <template #cell:select="{ row }">
          <input type="checkbox" :value="row.idx" v-model="selectedItems" />
        </template>
        <template #cell:no="{ index }">
          <span class="coupon-page__num">{{ getItemNumber(index) }}</span>
        </template>
        <template #cell:code="{ row }">
          <div class="coupon-page__code-cell">
            <code class="coupon-page__code">{{ row.code }}</code>
            <button class="p-icon-btn" type="button" v-tooltip title="코드 복사" @click="copyCode(row.code)">
              <i class="material-icons-round">content_copy</i>
            </button>
          </div>
        </template>
        <template #cell:name="{ row }"><span class="coupon-page__name">{{ row.name }}</span></template>
        <template #cell:amount="{ row }">
          <span v-if="row.discount_type === 'percent'" class="coupon-page__amount">{{ row.amount }}%</span>
          <span
            v-else
            v-tooltip="'폐지된 고정금액 쿠폰입니다. 화폐 정보가 없어 할인값을 표시할 수 없습니다.'"
            class="coupon-page__amount coupon-page__amount--void"
          >-</span>
        </template>
        <template #cell:target="{ row }">
          <APill v-if="row.learner_idx" kind="default">
            <i class="material-icons-round coupon-page__tag-icon">person</i>
            {{ row.learner_name || '지정' }}
          </APill>
          <APill v-else kind="ok" dot>범용</APill>
        </template>
        <template #cell:status="{ row }">
          <APill :kind="getStatusPillKind(row)" dot>{{ getStatusText(row) }}</APill>
        </template>
        <template #cell:expired="{ row }">
          <span class="coupon-page__mono">{{ getExpiredText(row) }}</span>
        </template>
        <template #cell:maxUsers="{ row }">
          <span v-if="row.max_users" class="coupon-page__mono coupon-page__issued">
            {{ formatNumber(row.issued_count) }}/{{ formatNumber(row.max_users) }}
          </span>
          <span v-else class="coupon-page__mono coupon-page__muted">-</span>
        </template>
        <template #cell:created="{ row }">
          <span class="coupon-page__mono coupon-page__muted">{{ formatDate(row.created_at) }}</span>
        </template>
        <template #cell:actions="{ row }">
          <div class="coupon-page__actions">
            <button
              class="p-icon-btn"
              :class="{ 'is-disabled': row.discount_type !== 'percent' }"
              type="button"
              v-tooltip
              :title="row.discount_type === 'percent' ? '수정' : '폐지된 고정금액 쿠폰은 수정할 수 없습니다'"
              @click="openEditModal(row)"
            >
              <i class="material-icons-round">edit</i>
            </button>
            <button class="p-icon-btn" type="button" v-tooltip title="코드 복사" @click="copyCode(row.code)">
              <i class="material-icons-round">content_copy</i>
            </button>
            <button class="p-icon-btn p-icon-btn--danger" type="button" v-tooltip title="삭제" @click="deleteItem(row)">
              <i class="material-icons-round">delete</i>
            </button>
          </div>
        </template>
      </ATable>

      <APagination
        v-if="totalCount > 0"
        :current-page="currentPage"
        :total="totalCount"
        :page-size="itemsPerPage"
        @change="(p) => { currentPage = p; getData(); }"
      />
    </ACard>

    <!-- 모달 (자식 컴포넌트 그대로) -->
    <CouponCreateModal
      :visible="showCreateModal"
      :editing-coupon="editingCoupon"
      @close="closeCreateModal"
      @success="handleModalSuccess"
    />
    <CouponBulkModal
      :visible="showBulkModal"
      @close="showBulkModal = false"
      @success="handleModalSuccess"
    />
  </div>
</template>

<script>
import SearchArea from "@/components/SearchArea.vue";
import CouponCreateModal from "./components/CouponCreateModal.vue";
import CouponBulkModal from "./components/CouponBulkModal.vue";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";

export default {
  name: "CouponManager",
  components: { SearchArea, CouponCreateModal, CouponBulkModal, APageHead, ABtn, ACard, AStat, APill, ATable, AEmpty, APagination },
  data() {
    return {
      tableData: [],
      totalCount: 0,
      currentPage: 1,
      itemsPerPage: 10,
      searchValues: {},
      activeTab: "all",
      stats: { total: 0, general: 0, assigned: 0, thisMonth: 0, active: 0, used: 0, expired: 0 },
      selectedItems: [],
      showCreateModal: false,
      showBulkModal: false,
      editingCoupon: null,
      tabs: [
        { value: "all", label: "전체", icon: "list", count: 0 },
        { value: "active", label: "활성", icon: "check_circle", count: 0 },
        { value: "used", label: "사용됨", icon: "done_all", count: 0 },
        { value: "expired", label: "만료됨", icon: "event_busy", count: 0 },
      ],
      searchFields: [
        { key: "code", placeholder: "쿠폰코드", col: "col-md-2" },
        { key: "name", placeholder: "쿠폰명", col: "col-md-2" },
        {
          key: "target_type", placeholder: "대상", type: "select", col: "col-md-2",
          options: [
            { value: "", label: "전체" },
            { value: "general", label: "범용" },
            { value: "assigned", label: "지정" },
          ],
        },
        { key: "period", placeholder: "등록기간", type: "period", col: "col-md-3" },
      ],
      columns: [
        { key: "select", label: "", align: "center", width: 40 },
        { key: "no", label: "번호", align: "center", width: 60 },
        { key: "code", label: "쿠폰코드", width: 200 },
        { key: "name", label: "쿠폰명", wrap: true },
        { key: "amount", label: "할인율", align: "right", width: 100 },
        { key: "target", label: "대상", width: 130 },
        { key: "status", label: "상태", width: 100 },
        { key: "expired", label: "만료일", width: 110 },
        { key: "maxUsers", label: "사용자수", align: "center", width: 90 },
        { key: "created", label: "등록일", width: 110 },
        { key: "actions", label: "관리", align: "right", width: 130 },
      ],
    };
  },
  computed: {
    getItemNumber() {
      return (index) => this.totalCount - ((this.currentPage - 1) * this.itemsPerPage + index);
    },
  },
  mounted() {
    this.getData();
    this.getStats();
  },
  methods: {
    async getData() {
      try {
        const params = {
          limit: this.itemsPerPage,
          page: this.currentPage,
          search_params: JSON.stringify({
            ...this.searchValues,
            status: this.activeTab !== "all" ? this.activeTab : undefined,
          }),
        };
        const res = await this.$axios({ method: "get", url: "/coupons", params, loadingBar: true });
        if (res.status === 200) {
          this.tableData = res.data.items || [];
          this.totalCount = typeof res.data.total_count === "object"
            ? res.data.total_count?.count || 0
            : res.data.total_count || 0;
          this.selectedItems = [];
        }
      } catch (error) {
        console.error("쿠폰 목록 조회 실패:", error);
      }
    },
    async getStats() {
      try {
        const res = await this.$axios({ method: "get", url: "/coupons/stats" });
        if (res.status === 200) {
          this.stats = res.data;
          this.tabs[0].count = res.data.total || 0;
          this.tabs[1].count = res.data.active || 0;
          this.tabs[2].count = res.data.used || 0;
          this.tabs[3].count = res.data.expired || 0;
        }
      } catch (error) {
        console.error("통계 조회 실패:", error);
      }
    },
    handleSearch(values) {
      this.searchValues = values;
      this.currentPage = 1;
      this.getData();
    },
    handleTabChange(tab) {
      this.activeTab = tab;
      this.currentPage = 1;
      this.getData();
    },
    openCreateModal() { this.editingCoupon = null; this.showCreateModal = true; },
    openEditModal(item) {
      // 폐지된 고정금액 쿠폰은 화폐를 잃어 amount 가 할인율이 아니다 ($2 → 2%로 저장될 위험)
      if (item.discount_type !== "percent") {
        this.$notifyVue({ type: "warning", title: "폐지된 고정금액 쿠폰은 수정할 수 없습니다." });
        return;
      }
      this.editingCoupon = { ...item };
      this.showCreateModal = true;
    },
    closeCreateModal() { this.showCreateModal = false; this.editingCoupon = null; },
    openBulkModal() { this.showBulkModal = true; },
    handleModalSuccess() { this.getData(); this.getStats(); },
    async deleteItem(item) {
      const requestFn = () => this.$axios({ method: "delete", url: `/coupons/${item.idx}` });
      const result = await this.$showConfirmationAndSubmit(requestFn, null,
        { title: "쿠폰을 삭제하시겠습니까?", text: `"${item.name}" 쿠폰이 삭제됩니다.` });
      if (result === "success") {
        this.$notifyVue({ type: "success", title: "쿠폰이 삭제되었습니다." });
        this.getData(); this.getStats();
      }
    },
    async deleteBulk() {
      if (this.selectedItems.length === 0) return;
      const requestFn = () => this.$axios({
        method: "delete", url: "/coupons/bulk",
        data: { ids: this.selectedItems },
      });
      const result = await this.$showConfirmationAndSubmit(requestFn, null,
        { title: "선택한 쿠폰을 삭제하시겠습니까?", text: `${this.selectedItems.length}개의 쿠폰이 삭제됩니다.` });
      if (result === "success") {
        this.$notifyVue({ type: "success", title: "선택한 쿠폰이 삭제되었습니다." });
        this.getData(); this.getStats();
      }
    },
    async downloadExcel() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/coupons/download",
          params: {
            search_params: JSON.stringify({
              ...this.searchValues,
              status: this.activeTab !== "all" ? this.activeTab : undefined,
            }),
          },
          responseType: "blob",
          loadingBar: true,
        });
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `쿠폰목록_${this.formatDateForFile(new Date())}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("엑셀 다운로드 실패:", error);
        this.$notifyVue({ type: "danger", title: "엑셀 다운로드에 실패했습니다." });
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code);
        this.$notifyVue({ type: "success", title: "쿠폰 코드가 복사되었습니다." });
      } catch (error) {
        const ta = document.createElement("textarea");
        ta.value = code; document.body.appendChild(ta);
        ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
        this.$notifyVue({ type: "success", title: "쿠폰 코드가 복사되었습니다." });
      }
    },
    // 우선순위: 사용됨 > 만료됨 > 소진됨 > 비활성 > 활성
    isExpired(item) {
      return !!item.expired_at && new Date(item.expired_at) < new Date();
    },
    isExhausted(item) {
      // 정원(max_users)이 있는 쿠폰만 소진 판정 — 만료일 마감 쿠폰은 정원이 없다
      return item.max_users != null && (item.issued_count || 0) >= item.max_users;
    },
    getStatusPillKind(item) {
      if (item.used_yn === "Y") return "default";
      if (this.isExpired(item)) return "warn";
      if (this.isExhausted(item)) return "info";
      if (item.active_yn === "N") return "dark";
      return "ok";
    },
    getStatusText(item) {
      if (item.used_yn === "Y") return "사용됨";
      if (this.isExpired(item)) return "만료됨";
      if (this.isExhausted(item)) return "소진됨";
      if (item.active_yn === "N") return "비활성";
      return "활성";
    },
    getExpiredText(item) {
      // 사용자수 마감 쿠폰은 만료일이 없고, 둘 다 비어 있으면 전환 이전 무기한 쿠폰이다
      if (item.max_users != null) return "-";
      return item.expired_at ? this.formatDate(item.expired_at) : "무기한";
    },
    formatNumber(n) { if (!n) return "0"; return Number(n).toLocaleString("ko-KR"); },
    formatDate(s) {
      if (!s) return "-";
      return new Date(s).toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" });
    },
    formatDateForFile(d) {
      return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.coupon-page {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__tabs { align-self: flex-start; }
  &__tab-icon { font-size: 14px !important; line-height: 1; }

  &__sec-title {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 13px; font-weight: 800; color: $p-ink;
  }

  &__sec-icon { color: $p-point-deep; font-size: 16px !important; }

  &__num { font-family: $p-font-mono; color: $p-ink-mid; font-weight: 700; }
  &__name { font-weight: 700; color: $p-ink; }
  &__amount { font-family: $p-font-mono; font-weight: 800; color: $p-point-deep; }
  &__amount--void { color: $p-ink-soft; font-weight: 700; cursor: help; }

  .p-icon-btn.is-disabled {
    opacity: 0.4;
    cursor: not-allowed;

    &:hover { background: $p-panel; border-color: $p-line; }
  }
  &__mono { font-family: $p-font-mono; color: $p-ink2; font-size: 11px; }
  &__issued { font-weight: 800; color: $p-ink-mid; }
  &__muted { color: $p-ink-soft; }

  &__code-cell {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  &__code {
    background: $p-point-soft;
    color: $p-point-deep;
    padding: 3px 8px;
    border-radius: 5px;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
  }

  &__tag-icon {
    font-size: 11px !important;
    margin-right: 2px;
  }

  &__actions {
    display: inline-flex;
    gap: 4px;
    justify-content: flex-end;
  }
}
</style>
